import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
// Removed mockData imports - using only real database data
import { useAuth } from '../context/AuthContext';
import {
  CalendarIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import MiniCalendar from '../components/MiniCalendar';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { groupSlug } = useParams<{ groupSlug?: string }>();
  const prefix = groupSlug ? `/${groupSlug}` : '';
  interface Event {
    id: string;
    title: string;
    date: string;
    description?: string;
  }
  
  interface Issue {
    id: string;
    title: string;
    status: string;
    priority?: string;
    createdAt: string;
  }
  
  interface Invoice {
    id: string;
    amount: number | string;
    description: string;
    status: string;
    dueDate?: string;
    createdAt: string;
  }
  
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);
  const [recentIssues, setRecentIssues] = useState<Issue[]>([]);
  const [recentPayments, setRecentPayments] = useState<Invoice[]>([]);
  const [dashboardStats, setDashboardStats] = useState([
    { label: 'Total Tenants', value: '0', icon: UserGroupIcon, trend: '', clickable: true, route: '/users' },
    { label: 'Monthly Revenue', value: '$0', icon: ArrowTrendingUpIcon, trend: '', clickable: true, route: '/payments' },
    { label: 'Open Issues', value: '0', icon: ExclamationTriangleIcon, trend: '', clickable: true, route: '/issues' },
    { label: 'Pending Payments', value: '0', icon: CreditCardIcon, trend: '', clickable: true, route: '/payments' },
  ]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Allow fetch even without propertyGroupId - backend will use owned groups for admins
      if (!user) return;
      
      try {
        const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
        const headers = selectedGroupId ? { 'x-selected-group': selectedGroupId } : {};
        
        // Fetch all data using same approach as individual pages
        const [eventsRes, issuesRes, invoicesRes, usersRes] = await Promise.all([
          api.get('/api/events', { headers }).catch(() => ({ data: [] })),
          api.get('/api/issues', { headers }).catch(() => ({ data: [] })),
          api.get('/api/invoices', { headers }).catch(() => ({ data: [] })),
          api.get('/api/users', { headers }).catch(() => ({ data: [] }))
        ]);
        
        const events = Array.isArray(eventsRes.data) ? eventsRes.data as Event[] : [];
        const issues = Array.isArray(issuesRes.data) ? issuesRes.data as Issue[] : [];
        const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data as Invoice[] : [];
        const users = Array.isArray(usersRes.data) ? usersRes.data as Array<{ role: string; [key: string]: any }> : [];
        
        setAllEvents(events);
        setRecentEvents(events.slice(0, 3));
        setRecentIssues(issues.filter(i => i.status === 'OPEN').slice(0, 3));
        setRecentPayments(invoices.slice(0, 3));
        
        // Update stats based on actual data
        const totalTenants = users.filter(u => u.role === 'RESIDENT').length;
        const monthlyRevenue = invoices.filter(i => i.status === 'PAID').reduce((sum, i) => sum + Number(i.amount || 0), 0);
        const openIssues = issues.filter(i => i.status === 'OPEN').length;
        const pendingPayments = invoices.filter(i => i.status === 'PENDING').length;
        
        setDashboardStats([
          { label: 'Total Tenants', value: totalTenants.toString(), icon: UserGroupIcon, trend: '', clickable: true, route: '/users' },
          { label: 'Monthly Revenue', value: `$${monthlyRevenue.toFixed(0)}`, icon: ArrowTrendingUpIcon, trend: '', clickable: true, route: '/payments' },
          { label: 'Open Issues', value: openIssues.toString(), icon: ExclamationTriangleIcon, trend: '', clickable: true, route: '/issues' },
          { label: 'Pending Payments', value: pendingPayments.toString(), icon: CreditCardIcon, trend: '', clickable: true, route: '/payments' },
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Show empty states when no data
        setAllEvents([]);
        setRecentEvents([]);
        setRecentIssues([]);
        setRecentPayments([]);
      }
    };
    
    fetchDashboardData();
  }, [user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      {/* Top Row: Quick Actions (2/3) + Mini Calendar (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 floating-card">
          <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              className="btn-secondary flex items-center justify-center space-x-2 py-4 hover:scale-105 transition-transform duration-200"
              onClick={() => navigate(`${prefix}/documents`)}
            >
              <DocumentIcon className="w-5 h-5" />
              <span>New Document</span>
            </button>
            <button
              className="btn-secondary flex items-center justify-center space-x-2 py-4 hover:scale-105 transition-transform duration-200"
              onClick={() => navigate(`${prefix}/events`)}
            >
              <CalendarIcon className="w-5 h-5" />
              <span>Schedule Event</span>
            </button>
            <button
              className="btn-secondary flex items-center justify-center space-x-2 py-4 hover:scale-105 transition-transform duration-200"
              onClick={() => navigate(`${prefix}/issues`)}
            >
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span>Report Issue</span>
            </button>
            <button
              className="btn-primary flex items-center justify-center space-x-2 py-4 hover:scale-105 transition-transform duration-200"
              onClick={() => navigate(`${prefix}/payments`)}
            >
              <CreditCardIcon className="w-5 h-5" />
              <span>Create Invoice</span>
            </button>
          </div>
        </div>

        <div className="lg:col-span-1">
          <MiniCalendar
            events={allEvents}
            onSelectDate={(_date, dayEvents) => {
              if (dayEvents.length > 0) navigate(`${prefix}/events`);
            }}
          />
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardStats.map((stat, index) => (
          <div 
            key={stat.label} 
            className={`floating-card group ${stat.clickable ? 'cursor-pointer hover:scale-105' : ''}`}
            onClick={() => stat.clickable && stat.route && navigate(`${prefix}${stat.route}`)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                {stat.trend && (
                  <div className="flex items-center mt-2">
                    <span className={`text-xs font-medium ${
                      stat.trend.startsWith('+') ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {stat.trend}
                    </span>
                    <span className="text-white/40 text-xs ml-1">vs last month</span>
                  </div>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <stat.icon className="w-6 h-6 text-[#22d3ee]" />
              </div>
            </div>
            {stat.clickable && (
              <div className="mt-2 text-xs text-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Click to view details →
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upcoming Events */}
        <div className="floating-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center mr-3">
              <CalendarIcon className="w-5 h-5 text-[#22d3ee]" />
            </div>
            <h2 className="text-xl font-semibold text-white">Upcoming Events</h2>
          </div>
          
          <div className="space-y-4">
            {recentEvents.length > 0 ? recentEvents.map((event: any, index: number) => (
              <div key={event.id} className="group">
                <div className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/5 transition-colors duration-200">
                  <div className="w-2 h-2 rounded-full bg-[#22d3ee] mt-2 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white group-hover:text-[#22d3ee] transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-sm text-white/60 mt-1">
                      {formatDate(event.date)} • {event.location}
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <p className="text-white/40 text-sm">No upcoming events</p>
              </div>
            )}
          </div>
          
          <button 
            className="w-full mt-4 btn-secondary text-sm"
            onClick={() => navigate(`${prefix}/events`)}
          >
            View All Events
          </button>
        </div>

        {/* Open Issues */}
        <div className="floating-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-red-500/20 to-orange-500/20 flex items-center justify-center mr-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Open Issues</h2>
          </div>
          
          <div className="space-y-4">
            {recentIssues.length > 0 ? recentIssues.map((issue: any) => (
              <div key={issue.id} className="group">
                <div className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/5 transition-colors duration-200">
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-red-400"></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white group-hover:text-red-400 transition-colors">
                      {issue.title}
                    </h3>
                    <p className="text-sm text-white/60 mt-1">
                      Created: {formatDate(issue.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <p className="text-white/40 text-sm">No open issues</p>
              </div>
            )}
          </div>
          
          <button 
            className="w-full mt-4 btn-secondary text-sm"
            onClick={() => navigate(`${prefix}/issues`)}
          >
            View All Issues
          </button>
        </div>

        {/* Payment Status */}
        <div className="floating-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center mr-3">
              <CreditCardIcon className="w-5 h-5 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Payment Status</h2>
          </div>
          
          <div className="space-y-4">
            {recentPayments.length > 0 ? recentPayments.slice(0, 3).map((payment: any, index: number) => (
              <div key={payment.id} className="group">
                <div className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors duration-200">
                  <div className="flex-1">
                    <h3 className="font-medium text-white text-sm">
                      {payment.description}
                    </h3>
                    <p className="text-xs text-white/60 mt-1">
                      ${Number(payment.amount).toFixed(2)} • Due: {payment.dueDate ? formatDate(payment.dueDate) : 'N/A'}
                    </p>
                  </div>
                  <span className={`
                    px-2 py-1 rounded-lg text-xs font-medium
                    ${payment.status === 'PAID' ? 'bg-green-500/20 text-green-400' : 
                      payment.status === 'OVERDUE' ? 'bg-red-500/20 text-red-400' : 
                      'bg-yellow-500/20 text-yellow-400'}
                  `}>
                    {payment.status}
                  </span>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <p className="text-white/40 text-sm">No recent payments</p>
              </div>
            )}
          </div>
          
          <button 
            className="w-full mt-4 btn-secondary text-sm"
            onClick={() => navigate(`${prefix}/payments`)}
          >
            View All Payments
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 